import { useCallback, useEffect, useState } from 'react';

import { INSIGHTS_CATALOG_URL } from '@/constants/urls';
import {
  getInsightsCatalogLayers,
  InsightsCatalogLayer,
} from '@/endpoints/insights-catalog/getInsightsCatalogLayers';
import { County } from '@/endpoints/itasca/county/getCounties';
import { State } from '@/endpoints/itasca/state/getState';

import { SearchResultType, URLSearchResult } from '../models';

export const useLayersSearch = (
  debouncedInput: string,
  {
    selectedCounty,
    selectedState,
  }: { selectedCounty?: County; selectedState?: State }
): {
  layers: URLSearchResult<InsightsCatalogLayer>[];
  layersSeeMore: number;
  isLoading: boolean;
} => {
  const [layers, setLayers] = useState<URLSearchResult<InsightsCatalogLayer>[]>(
    []
  );
  const [layersSeeMore, setLayersSeeMore] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setLayers([]);
    setLayersSeeMore(0);
  }, [debouncedInput]);

  const getLayers = useCallback(
    async (signal: AbortSignal) => {
      if (!debouncedInput && !selectedState && !selectedCounty) {
        setLayers([]);
        setLayersSeeMore(0);
        return;
      }

      setIsLoading(true);

      const { res, err } = await getInsightsCatalogLayers(
        {
          full_fips__overlap: selectedCounty
            ? [selectedCounty?.full_fips]
            : undefined,
          limit: 5,
          search: debouncedInput,
          state_fips__overlap: selectedState
            ? [selectedState?.state_fips]
            : undefined,
        },
        signal
      );

      if (err) {
        setLayers([]);
      }

      if (res?.data) {
        setLayers(
          res.data.results.map((x) => ({
            id: `layer-${x.id}`,
            label: x.name,
            original: x,
            type: SearchResultType.LAYER_LIBRARY,
            url: `${INSIGHTS_CATALOG_URL}/${x.id}?zoom=true`,
          }))
        );
        setLayersSeeMore(res.data.count);
      }

      setIsLoading(false);
    },
    [debouncedInput, selectedCounty, selectedState]
  );

  useEffect(() => {
    const abortController = new AbortController();

    getLayers(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [getLayers]);

  return {
    isLoading,
    layers,
    layersSeeMore,
  };
};
